import "./InputTextArea.scss";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { VAR_TYPES } from "constants/constants";
import { useEffect, useRef, useState } from "react";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";

const InputTextArea = ({
  label,
  placeholder,
  readOnly,
  required,
  type,
  value,
  width,
  errors,
  isMission,
  id,
  inputStyle,
  dynamicHeight,
  defaultDynamicHeight,
  className,
  rows,
  isExport,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const intl = useIntl();
  const [val, setVal] = useState((isMission ? "- " : "") || value);

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const containerRef = useRef(null);
  const textareaRef = useRef(null);

  const resizeTextarea = (
    textarea,
    heightOffset = defaultDynamicHeight || 60
  ) => {
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;

    if (containerRef.current) {
      containerRef.current.style.height = `${
        textarea.scrollHeight + heightOffset
      }px`;
    }
  };

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    if ((dynamicHeight || isExport) && textareaRef.current) {
      resizeTextarea(textareaRef.current);
    }
  }, [defaultDynamicHeight, dynamicHeight, isExport, val]);

  const inputTextAreaStyle = inputStyle
    ? {
        ...inputStyle,
        fontWeight: isFocused ? "normal" : inputStyle.fontWeight,
      }
    : {};

  const handleInput = (e, defaultDynamicHeight) => {
    resizeTextarea(e.target, defaultDynamicHeight);
  };

  const inputValueStyle = {
    ...inputTextAreaStyle,
    height: isExport ? "auto" : props.height ?? defaultDynamicHeight ?? "",
    minHeight: "100px",
    maxHeight: isExport ? "none" : undefined,
    overflow: isExport ? "visible" : "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };

  return (
    <div
      className={`blocs ${className} ${isExport ? "export-mode" : ""}`}
      ref={containerRef}
      style={{
        width: width ? `${width}` : "310px",
        height: isExport ? "auto" : undefined,
        overflow: isExport ? "visible" : undefined,
      }}
      id={`${id}_container`}
    >
      <div
        className="overlap-group"
        style={
          isExport
            ? {
                height: "auto",
                padding: "16px",
              }
            : undefined
        }
      >
        {label && (
          <div className="label sourcesanspro-normal-shadow-blue-14px">
            <label className={errors?.ref ? "label-error" : "label-standard"}>
              {typeof label === "string" ? (
                label
              ) : (
                <FormattedMessage {...label} />
              )}
              {required && "*"}
            </label>
            {errors?.ref && (
              <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
            )}
          </div>
        )}
        {isExport ? (
          <div
            id={`input-area-${id}`}
            className="input-placeholder input-placeholder--export sourcesanspro-normal-gray-suit-18px"
            style={inputValueStyle}
            data-testid={props.name}
          >
            {val || ""}
          </div>
        ) : (
          <textarea
            rows={rows || "unset"}
            id={`input-area-${id}`}
            className="input-placeholder sourcesanspro-normal-gray-suit-18px"
            ref={(el) => {
              textareaRef.current = el;
            }}
            placeholder={
              typeof placeholder === VAR_TYPES.STRING
                ? placeholder
                : intl.formatMessage(placeholder)
            }
            type={type || "text"}
            name={props.name}
            style={{
              ...inputValueStyle,
              ...(isMission ? { whiteSpace: "pre-wrap" } : {}),
            }}
            onChange={(e) => {
              setVal(e.target.value);
              props.onChange(e);
            }}
            onInput={
              dynamicHeight && !isExport
                ? (e) => handleInput(e, defaultDynamicHeight)
                : undefined
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={val}
            disabled={readOnly}
            data-testid={props.name}
          />
        )}
      </div>
      {showErrorPopup && (
        <ErrorPopup
          errors={props.errors}
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          label={label}
          errorMessage={props?.errorMessage}
          errorValue={props?.errorValue}
          additionalErrorMessage={props?.additionalErrorMessage}
          additionalErrorValue={props?.additionalErrorValue}
        />
      )}
    </div>
  );
};

InputTextArea.propTypes = {
  dynamicHeight: PropTypes.bool,
  height: PropTypes.string,
  label: PropTypes.object,
  placeholder: PropTypes.object,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.object,
  isExport: PropTypes.bool,
};

InputTextArea.defaultProps = {
  dynamicHeight: false,
  label: null,
  placeholder: "",
  readOnly: false,
  required: false,
  type: null,
  value: "",
};

export default InputTextArea;
